/** The rt relay's protocol v2 (rt `docs/REALTIME_CLIENT_PROTOCOL.md`, the AI designer's `player/wire.py`). */
export type RtEvent = { type: string; key?: string; committed?: boolean; playerId?: number; data?: unknown; [field: string]: unknown };

export const PROTOCOL_VERSION = 2;
/** rt cuts by UTF-16 code units, the app by UTF-8 bytes: a frame under the byte limit satisfies both. */
export const MAX_CHUNK_BYTES = 48 * 1024;
const MAX_CHUNKS = 1024;
const MAX_PENDING = 8;

const encoder = new TextEncoder(), decoder = new TextDecoder();
let messages = 0;

/** One batch → the text frames to send: one array, or `Chunk` frames that never cut a character. */
export function encodeBatch(events: readonly RtEvent[]): string[] {
  // The stamp rides inside `data`: rt rebuilds the envelope and drops unknown top-level fields.
  const payload = JSON.stringify(events.map(event => event.data && typeof event.data === 'object' && !Array.isArray(event.data)
    ? { ...event, data: { ...event.data as Record<string, unknown>, v: (event.data as { v?: unknown }).v ?? PROTOCOL_VERSION } } : event));
  const raw = encoder.encode(payload);
  if (raw.length <= MAX_CHUNK_BYTES) return [payload];
  const pieces: string[] = [];
  for (let start = 0; start < raw.length;) {
    let end = Math.min(start + MAX_CHUNK_BYTES, raw.length);
    while (end < raw.length && (raw[end] & 0xc0) === 0x80) end--;
    pieces.push(decoder.decode(raw.subarray(start, end)));
    start = end;
  }
  const messageId = `web${++messages}`;
  return pieces.map((data, index) => JSON.stringify({ type: 'Chunk', messageId, index, total: pieces.length, data }));
}

const asEvents = (value: unknown): RtEvent[] | null => Array.isArray(value)
  ? value.filter((event): event is RtEvent => !!event && typeof event === 'object' && typeof (event as RtEvent).type === 'string')
  : value && typeof value === 'object' ? [value as RtEvent] : null;

function parse(text: string): unknown {
  try { return JSON.parse(text); } catch { return undefined; }
}

/** Inbound frames → whole batches, one assembler per socket: a message cut short by a drop never meets the next socket's. */
export class ChunkAssembler {
  private pending = new Map<string, { total: number; parts: Array<string | undefined>; left: number }>();

  /** The frame's events, or null while a chunked message is incomplete or when the frame is not one rt sends. */
  feed(text: string): RtEvent[] | null {
    const frame = parse(text) as { type?: unknown; messageId?: unknown; index?: unknown; total?: unknown; data?: unknown } | undefined;
    if (!frame || typeof frame !== 'object') return null;
    if (Array.isArray(frame) || frame.type !== 'Chunk') return asEvents(frame);
    const index = frame.index as number, total = frame.total as number, { data } = frame, id = String(frame.messageId);
    if (!Number.isInteger(total) || total < 1 || total > MAX_CHUNKS) return null;
    if (!Number.isInteger(index) || index < 0 || index >= total || typeof data !== 'string') return null;
    let message = this.pending.get(id);
    if (message && message.total !== total) return null;
    if (!message) {
      if (this.pending.size >= MAX_PENDING) this.pending.delete(this.pending.keys().next().value!);
      message = { total, parts: new Array(total), left: total };
      this.pending.set(id, message);
    }
    if (message.parts[index] === undefined) message.left--;
    message.parts[index] = data;
    if (message.left) return null;
    this.pending.delete(id);
    return asEvents(parse(message.parts.join('')));
  }
}

export type RtTerminal = 'closed' | 'superseded' | 'full';
export type CloseAction = { kind: 'reconnect' } | { kind: 'new-ticket' } | { kind: 'end'; terminal: RtTerminal };

/** What a close code asks of the client (the protocol's «Коды закрытия»). */
export function closeAction(code: number): CloseAction {
  if (code === 4003) return { kind: 'new-ticket' };
  if (code === 4008) return { kind: 'end', terminal: 'superseded' };
  if (code === 4009) return { kind: 'end', terminal: 'full' };
  if (code >= 4000 && code < 5000) return { kind: 'end', terminal: 'closed' };
  return { kind: 'reconnect' };
}
